import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Plus, BookOpen, Users, Star, DollarSign, Award, TrendingUp, Zap } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function CoachDashboard({ user }) {
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    try {
      const [coursesRes, enrollmentsRes] = await Promise.all([
        axios.get(`${API}/courses?coach_id=${user?.user_id}`, { withCredentials: true }),
        axios.get(`${API}/enrollments/my`, { withCredentials: true })
      ]);
      // Filter courses by current user
      const myCourses = coursesRes.data.filter(c => c.coach_id === user?.user_id);
      setCourses(myCourses);
      setEnrollments(enrollmentsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const totalStudents = enrollments.length;
  const totalRevenue = enrollments
    .filter(e => e.payment_status === 'paid')
    .reduce((sum, e) => sum + (e.course_price || 0), 0);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-deep-dark flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-3 border-premium-gold/20 border-t-premium-gold"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-deep-dark py-12" data-testid="coach-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4">
          <div>
            <span className="inline-block px-4 py-1 bg-premium-gold/20 text-premium-gold rounded-full text-sm font-semibold tracking-wider mb-2">
              Coach Portal
            </span>
            <h1 
              className="text-4xl md:text-5xl font-bold tracking-tight text-white"
              style={{ fontFamily: 'Fraunces, serif' }}
              data-testid="dashboard-title"
            >
              Coach <span className="gold-gradient">dashboard</span>
            </h1>
            <p className="text-white/60 mt-2">Manage your courses and students</p>
          </div>
          <div className="flex gap-3">
            <Link
              to="/coach/credentials"
              className="bg-dark-surface hover:bg-dark-surface-light text-white font-semibold px-6 py-3 flex items-center gap-2 transition-all duration-300 rounded-lg border border-premium-gold/30"
              data-testid="credentials-link"
            >
              <Award className="w-5 h-5 text-premium-gold" strokeWidth={1.5} />
              My credentials
            </Link>
            <Link to="/coach/courses/new">
              <button 
                className="bg-premium-gold hover:bg-premium-gold-dark text-deep-dark font-bold tracking-wider px-6 py-3 transition-all duration-300 rounded-lg hover:shadow-glow-gold flex items-center gap-2"
                data-testid="create-course-button"
              >
                <Plus className="w-5 h-5" strokeWidth={1.5} />
                New course
              </button>
            </Link>
          </div>
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <div className="bg-dark-surface p-6 rounded-xl border border-premium-gold/20" data-testid="stat-courses">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-soft-lavender font-semibold mb-1">Active courses</p>
                <p className="text-4xl font-black text-white" style={{ fontFamily: 'Fraunces, serif' }}>
                  {courses.length}
                </p>
              </div>
              <BookOpen className="w-8 h-8 text-premium-gold" strokeWidth={1.5} />
            </div>
          </div>
          
          <div className="bg-dark-surface p-6 rounded-xl border border-premium-gold/20" data-testid="stat-students">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-soft-lavender font-semibold mb-1">Total students</p>
                <p className="text-4xl font-black text-white" style={{ fontFamily: 'Fraunces, serif' }}>
                  {totalStudents}
                </p>
              </div>
              <Users className="w-8 h-8 text-premium-gold" strokeWidth={1.5} />
            </div>
          </div>
          
          <div className="bg-dark-surface p-6 rounded-xl border border-premium-gold/20" data-testid="stat-rating">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-soft-lavender font-semibold mb-1">Avg rating</p>
                <p className="text-4xl font-black text-white" style={{ fontFamily: 'Fraunces, serif' }}>
                  {courses.length > 0
                    ? (courses.reduce((sum, c) => sum + c.rating, 0) / courses.length).toFixed(1)
                    : '0.0'}
                </p>
              </div>
              <Star className="w-8 h-8 text-premium-gold" strokeWidth={1.5} />
            </div>
          </div>
          
          <div className="bg-dark-surface p-6 rounded-xl border border-premium-gold/20" data-testid="stat-revenue">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-soft-lavender font-semibold mb-1">Revenue</p>
                <p className="text-4xl font-black text-white" style={{ fontFamily: 'Fraunces, serif' }}>
                  ₹{totalRevenue.toFixed(0)}
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-premium-gold" strokeWidth={1.5} />
            </div>
          </div>
        </div>
        
        {/* My Courses */}
        <div>
          <h2 
            className="text-3xl font-bold tracking-tight text-white mb-6 flex items-center gap-3"
            style={{ fontFamily: 'Fraunces, serif' }}
          >
            <Zap className="w-7 h-7 text-premium-gold" />
            My Courses
          </h2>
          
          {courses.length === 0 ? (
            <div className="bg-dark-surface p-12 text-center rounded-xl border border-premium-gold/30/50" data-testid="no-courses">
              <BookOpen className="w-16 h-16 text-white/20 mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-xl text-white/60 mb-6">You haven't created any courses yet</p>
              <Link to="/coach/courses/new">
                <button className="bg-premium-gold hover:opacity-90 text-deep-dark font-bold tracking-wider px-8 py-3 rounded-lg transition-all duration-300 hover:shadow-glow-gold">
                  Create your first course
                </button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {courses.map((course) => (
                <div key={course.course_id} className="bg-dark-surface rounded-xl overflow-hidden border border-premium-gold/30/50 hover:border-premium-gold/30 transition-all duration-300" data-testid={`course-${course.course_id}`}>
                  <div className="h-40 bg-deep-dark overflow-hidden relative">
                    {course.image_url ? (
                      <img src={course.image_url} alt={course.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-imperial to-dark-purple">
                        <BookOpen className="w-12 h-12 text-white/20" strokeWidth={1.5} />
                      </div>
                    )}
                    <div className="absolute top-4 left-4">
                      <span className="text-xs font-bold tracking-wide text-white bg-premium-gold px-3 py-1 rounded-full">
                        {course.level}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      {course.rating > 0 && (
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-premium-gold fill-current" strokeWidth={1.5} />
                          <span className="text-sm font-bold text-premium-gold">{course.rating.toFixed(1)}</span>
                        </div>
                      )}
                    </div>
                    <h3 className="text-xl font-bold mb-3 text-white" style={{ fontFamily: 'Fraunces, serif' }}>
                      {course.title}
                    </h3>
                    <div className="flex items-center justify-between mb-4 pb-4 border-b border-premium-gold/30/50">
                      <span className="text-sm text-white/60 flex items-center gap-1">
                        <Users className="w-4 h-4" strokeWidth={1.5} />
                        {course.enrolled_count} students
                      </span>
                      <span className="text-lg font-bold text-premium-gold">
                        ₹{course.price.toFixed(0)}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Link to={`/courses/${course.course_id}`} className="flex-1">
                        <button className="w-full bg-deep-dark hover:bg-deep-dark text-white font-semibold py-2.5 rounded-lg transition-colors border border-premium-gold/30/50">
                          View
                        </button>
                      </Link>
                      <Link to={`/coach/courses/${course.course_id}/edit`} className="flex-1">
                        <button className="w-full bg-premium-gold hover:bg-premium-gold-dark text-white font-semibold py-2.5 rounded-lg transition-all duration-300">
                          Edit
                        </button>
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Recent Students */}
        {enrollments.length > 0 && (
          <div className="mt-12">
            <h2 
              className="text-3xl font-bold tracking-tight text-white mb-6 flex items-center gap-3"
              style={{ fontFamily: 'Fraunces, serif' }}
            >
              <Users className="w-7 h-7 text-premium-gold" strokeWidth={1.5} />
              Recent Students
            </h2>
            <div className="bg-dark-surface rounded-xl overflow-hidden border border-premium-gold/30/50">
              <table className="w-full">
                <thead className="bg-deep-dark/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-bold tracking-wide text-white/70">Student</th>
                    <th className="px-6 py-4 text-left text-xs font-bold tracking-wide text-white/70">Course</th>
                    <th className="px-6 py-4 text-left text-xs font-bold tracking-wide text-white/70">Enrolled</th>
                    <th className="px-6 py-4 text-left text-xs font-bold tracking-wide text-white/70">Progress</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-purple-800/30">
                  {enrollments.slice(0, 10).map((enrollment) => (
                    <tr key={enrollment.enrollment_id} className="hover:bg-deep-dark/30 transition-colors" data-testid={`student-row-${enrollment.enrollment_id}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {enrollment.student_picture ? (
                            <img src={enrollment.student_picture} alt={enrollment.student_name} className="w-10 h-10 rounded-full border-2 border-premium-gold/30" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-deep-dark border-2 border-premium-gold/30"></div>
                          )}
                          <span className="font-semibold text-white">{enrollment.student_name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-white/60">{enrollment.course_title}</td>
                      <td className="px-6 py-4 text-white/60">
                        {new Date(enrollment.enrolled_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-24 bg-deep-dark rounded-full h-2 overflow-hidden">
                            <div 
                              className="bg-gradient-to-r from-premium-gold to-yellow-400 h-2 rounded-full"
                              style={{ width: `${enrollment.progress_percentage}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-bold text-premium-gold">{enrollment.progress_percentage}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CoachDashboard;
